import React from 'react'
import { Card, CardBody, Heading, Text, Image, Stack, IconButton, Box, Icon } from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { useMusicPlayer } from '../../context/MusicPlayerContext';
import { BeatIcon } from '../../tools/animatedIcons';

import {FaApple, FaPlay, FaSpotify , FaAmazon, FaYoutube, FaDeezer } from "react-icons/fa";
import { SlBasket } from "react-icons/sl";
import { SiPandora, SiTidal } from "react-icons/si";

const platformIcons = {
  spotify: <FaSpotify />,
  apple: <FaApple />,
  amazon: <FaAmazon />,
  youtube: <FaYoutube />,
  tidal: <SiTidal />,
  deezer: <FaDeezer />,
  pandora: <SiPandora />,
};

const platformUrls = {
  spotify: (id) => `https://open.spotify.com/track/${id}`,
  apple: (trackName, id) => `https://music.apple.com/us/song/${trackName}/${id}`,
  amazon: (id) => `https://music.amazon.com/albums/${id}`,
  youtube: (id) => `https://music.youtube.com/watch?v=${id}`,
  tidal: (id) => `https://tidal.com/browse/track/${id}`,
  deezer: (id) => `https://www.deezer.com/track/${id}`,
  pandora: (id) => `https://www.pandora.com/track/${id}`,
};

export default function BrowseCard({ track, handleClick, featured }) {
    const { playTrack, isPlaying, currentTrack } = useMusicPlayer();
    return (
       
        <Card 
            as={Link} 
            to={`/track/${track.track_id}`} 
            maxW={{ base: "350px", md: "300px", lg: "320px" }}
            w={{ base: "100%", md: "280px", lg: "300px" }}
            h={{ base: "420px", md: "450px", lg: "470px" }}
            mx={{ base: "auto", sm: "0" }}
            scrollSnapAlign="start" 
            p={3} 
            bg="transparent" 
            boxShadow="lg" 
            border="2px" 
            borderColor="dark.500" 
            position="relative"
            _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
            transition="all 0.3s ease"
        > 
            <CardBody p={3} display="flex" flexDirection="column" h="100%">
                {featured && (
                    <Box
                        position="absolute"
                        top="2"
                        right="2"
                        bg="primary.900"
                        color="white"
                        px="2"
                        py="1"
                        borderRadius="md"
                        fontSize="xs"
                        fontWeight="bold"
                        zIndex="1"
                    >
                        FEATURED
                    </Box>
                )}
                <Image
                    src={track.image_url}
                    w="100%"
                    h={{ base: "180px", md: "200px", lg: "220px" }}
                    objectFit="cover"
                    alt={track.track_name}
                    borderRadius='lg'
                    transition="transform 0.3s ease-in-out"
                    _hover={{ transform: "scale(1.05)" }}
                />
                <Stack mt='6' spacing='3' flex="1">
                    <Heading color="primary.900" noOfLines={1} size='sm'>{track.track_name}</Heading>
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ base: 2, md: 3 }} flexWrap="wrap">
                        <Text color='primary.900' fontSize={{ base: 'lg', md: 'xl' }}>
                            $ {track.price_per_share} <Text as="i" display="inline" fontSize={{ base: 'sm', md: 'm' }}>per share</Text>
                        </Text>
                        <Box display="flex" gap={{ base: 1, md: 2 }}>

                        <IconButton 
                            isDisabled={track.track_url === "" && track.preview_url === ""} 
                            variant="solid" 
                            size={{ base: "sm", md: "md" }}
                            onClick={(e) => {
                                e.preventDefault();
                                playTrack({
                                    name: track.track_name,
                                    artist: track.artist_name,
                                    image: track.image_url,
                                    url: track.preview_url ? track.preview_url : track.track_url,
                                    track_id: track.track_id,
                                    artist_id: track.publisher.id
                                })
                            }} 
                            icon={isPlaying && currentTrack.name === track.track_name ? <BeatIcon /> : <Icon as={FaPlay} boxSize={{ base: 4, md: 6 }} color="white" />} 
                            alignSelf="flex-start"
                        >
                            Play
                        </IconButton>
                        <IconButton 
                            variant="solid" 
                            size={{ base: "sm", md: "md" }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleClick()
                            }} 
                            icon={<SlBasket />} 
                            alignSelf="flex-start"
                        >
                            Add to Basket
                        </IconButton>
                        </Box>
                    </Box>

                    {track.streaming_platforms && track.streaming_platforms.length > 0 && (
                        <Box>
                            <Text 
                                color="primary.900" 
                                fontSize={{ base: 'sm', md: 'md' }} 
                                fontWeight="semibold" 
                                mb={2}
                            >
                                Streaming on:
                            </Text>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                {track.streaming_platforms.map((platform, index) =>
                                    platform.platform && platform.platform in platformIcons ? (
                                        <IconButton
                                            key={index}
                                            size="sm"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const openUrl = platformUrls[platform.platform];
                                                if (openUrl) {
                                                    const url =
                                                        platform.platform === "apple"
                                                            ? openUrl(track.track_name, platform.id)
                                                            : openUrl(platform.id);
                                                    window.open(url, "_blank");
                                                }
                                            }}
                                            variant="ghost"
                                            bg="secondary.900"
                                            icon={platformIcons[platform.platform]}
                                            aria-label={`Listen on ${platform.platform}`}
                                            _hover={{ 
                                                bg: "primary.900", 
                                                transform: "scale(1.1)" 
                                            }}
                                            transition="all 0.2s ease"
                                        />
                                    ) : null
                                )}
                            </Box>
                        </Box>
                    )}
                </Stack>
            </CardBody>
        </Card>
    )
}